import { LocalDate } from "@js-joda/core";
import type { MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import { AvatarChip } from "components/designV2/avatarChip/avatarChip";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { Checkbox } from "components/designV2/checkbox/checkbox";
import { ListRow } from "components/designV2/listRow/listRow";
import { Modal } from "components/designV2/modal/modal";
import { RadioButton } from "components/designV2/radioButton/radioButton";
import { Text } from "components/designV2/text/text";
import { ColorV2 } from "config/colors";
import type { CompanyWorker } from "model/Company";
import { getDateLocale } from "web-apps/company/utils/utils";
import {
  GenerateSalaryNoticesModalContent,
  GenerateSalaryNoticesOptions,
  GenerateSalaryNoticesWorkerAction,
  GenerateSalaryNoticesWorkerCount,
  GenerateSalaryNoticesWorkerList,
} from "./salary.styled";
import type {
  GenerateSalaryNoticesMode,
  GenerateSalaryNoticesModalState,
} from "./useGenerateSalaryNoticesModal";

interface GenerateSalaryNoticesModalProps {
  readonly avatarUrls: Readonly<Record<string, string>>;
  readonly canCreateSalaryNotices: boolean;
  readonly cutOffDate: LocalDate;
  readonly generateSalaryNoticesMode: GenerateSalaryNoticesMode;
  readonly isCutOffWarningOpen: boolean;
  readonly isGeneratingSalaryNotices: boolean;
  readonly isOpen: boolean;
  readonly modalState: GenerateSalaryNoticesModalState;
  readonly salaryNoticeWorkers: readonly CompanyWorker[];
  readonly selectedWorkerIds: readonly number[];
  readonly onCancelCutOffWarning: () => void;
  readonly onClose: () => void;
  readonly onConfirmCreate: () => Promise<void>;
  readonly onCreate: () => Promise<void>;
  readonly onModeChange: (value: string) => void;
  readonly onToggleWorker: (workerId: number) => void;
  readonly onWorkerActionClick: (event: MouseEvent<HTMLDivElement>) => void;
}

interface FormatCutOffDateParams {
  readonly date: LocalDate;
  readonly language: string;
}

const getWorkerName = (worker: CompanyWorker): string => {
  const name = [worker.firstName, worker.lastName]
    .filter((part): part is string => Boolean(part))
    .join(" ")
    .trim();
  return name || `ID ${worker.id}`;
};

const formatCutOffDate = ({ date, language }: FormatCutOffDateParams): string =>
  new Intl.DateTimeFormat(getDateLocale(language), {
    day: "numeric",
    month: "long",
  }).format(new Date(date.year(), date.monthValue() - 1, date.dayOfMonth()));

const assertNever = (value: never): never => {
  throw new Error(
    `Unhandled generate salary notices modal state: ${JSON.stringify(value)}`,
  );
};

export const GenerateSalaryNoticesModal = (
  props: GenerateSalaryNoticesModalProps,
): JSX.Element => {
  const { i18n, t } = useTranslation();
  const cutOffDate = formatCutOffDate({
    date: props.cutOffDate,
    language: i18n.language,
  });

  const renderFooter = (): JSX.Element => {
    if (props.isCutOffWarningOpen) {
      return (
        <>
          <SecondaryButton
            label={t("Salary.Cancel")}
            onClick={props.onCancelCutOffWarning}
            size="small"
            disabled={props.isGeneratingSalaryNotices}
          />
          <PrimaryButton
            label={t("Salary.GenerateSalaryNoticesCreateAnyway")}
            onClick={() => void props.onConfirmCreate()}
            size="small"
            isLoading={props.isGeneratingSalaryNotices}
          />
        </>
      );
    }

    return (
      <>
        <SecondaryButton
          label={t("Salary.Cancel")}
          onClick={props.onClose}
          size="small"
          disabled={props.isGeneratingSalaryNotices}
        />
        <PrimaryButton
          label={t("Salary.GenerateSalaryNoticesCreate")}
          onClick={() => void props.onCreate()}
          size="small"
          disabled={!props.canCreateSalaryNotices}
          isLoading={props.isGeneratingSalaryNotices}
        />
      </>
    );
  };

  const renderWorkerList = (): JSX.Element => (
    <>
      <GenerateSalaryNoticesWorkerCount>
        {props.generateSalaryNoticesMode === "all"
          ? t("Salary.GenerateSalaryNoticesAllWorkersCount", {
              count: props.salaryNoticeWorkers.length,
            })
          : t("Salary.GenerateSalaryNoticesSelectedWorkersCount", {
              count: props.selectedWorkerIds.length,
              total: props.salaryNoticeWorkers.length,
            })}
      </GenerateSalaryNoticesWorkerCount>
      <GenerateSalaryNoticesWorkerList>
        {props.salaryNoticeWorkers.map((worker) => {
          const isSelected = props.selectedWorkerIds.includes(worker.id);
          return (
            <ListRow
              key={worker.id}
              variant="dark"
              selected={isSelected}
              onClick={
                props.generateSalaryNoticesMode === "selected"
                  ? () => props.onToggleWorker(worker.id)
                  : undefined
              }
            >
              <AvatarChip
                title={getWorkerName(worker)}
                subTitle={`[ID: ${worker.id}]`}
                avatarUrl={
                  worker.externalId
                    ? props.avatarUrls[worker.externalId]
                    : undefined
                }
              />
              {props.generateSalaryNoticesMode === "selected" && (
                <GenerateSalaryNoticesWorkerAction
                  onClick={props.onWorkerActionClick}
                >
                  <Checkbox
                    checked={isSelected ? "checked" : "unchecked"}
                    onChange={() => props.onToggleWorker(worker.id)}
                  />
                </GenerateSalaryNoticesWorkerAction>
              )}
            </ListRow>
          );
        })}
      </GenerateSalaryNoticesWorkerList>
    </>
  );

  const renderWorkerState = (): JSX.Element => {
    switch (props.modalState) {
      case "loading":
        return (
          <Text typography="s1" color={ColorV2.Blue4}>
            {t("Salary.GenerateSalaryNoticesLoadingWorkers")}
          </Text>
        );
      case "error":
        return (
          <Text typography="s1" color={ColorV2.NegativeRed}>
            {t("Salary.GenerateSalaryNoticesWorkerLoadError")}
          </Text>
        );
      case "empty":
        return (
          <Text typography="s1" color={ColorV2.Blue4}>
            {t("Salary.GenerateSalaryNoticesNoActiveWorkers")}
          </Text>
        );
      case "list":
        return renderWorkerList();
      default:
        return assertNever(props.modalState);
    }
  };

  return (
    <Modal
      isOpen={props.isOpen}
      onClose={props.onClose}
      label={t(
        props.isCutOffWarningOpen
          ? "Salary.GenerateSalaryNoticesCutOffWarningTitle"
          : "Salary.GenerateSalaryNoticesTitle",
      )}
      size="small"
      showCloseButton={false}
      footer={renderFooter()}
    >
      {props.isCutOffWarningOpen ? (
        <GenerateSalaryNoticesModalContent>
          <Text typography="b2" color={ColorV2.Blue3}>
            {t("Salary.GenerateSalaryNoticesCutOffWarningDescription", {
              cutOffDate,
            })}
          </Text>
          <Text typography="b2" color={ColorV2.Blue3}>
            {t("Salary.GenerateSalaryNoticesCutOffWarningVisibility")}
          </Text>
        </GenerateSalaryNoticesModalContent>
      ) : (
        <GenerateSalaryNoticesModalContent>
          <Text typography="b2" color={ColorV2.Blue3}>
            {t("Salary.GenerateSalaryNoticesDescription")}
          </Text>
          <GenerateSalaryNoticesOptions role="radiogroup">
            <RadioButton
              name="generateSalaryNoticesMode"
              value="all"
              label={t("Salary.GenerateSalaryNoticesAllOption")}
              checked={props.generateSalaryNoticesMode === "all"}
              onChange={props.onModeChange}
            />
            <RadioButton
              name="generateSalaryNoticesMode"
              value="selected"
              label={t("Salary.GenerateSalaryNoticesSelectedOption")}
              checked={props.generateSalaryNoticesMode === "selected"}
              onChange={props.onModeChange}
            />
          </GenerateSalaryNoticesOptions>
          {renderWorkerState()}
        </GenerateSalaryNoticesModalContent>
      )}
    </Modal>
  );
};
